import { stockEventMatchesBusiness } from '../stockEventBusinessScope.js';
import { productionBatchLot } from '../productionOutput.js';

export interface ProductionCandidate {
  id: string;
  business_id: string | null;
  output_name: string;
  output_qty: number | null;
  output_uom: string | null;
  produced_at: string;
  expires_at: string | null;
  recipe_id: string | null;
  lp_code: string | null;
  archived_at: string | null;
}

export interface SelectProductionCandidatesInput {
  productions: readonly ProductionCandidate[];
  activeBusinessId: string | null;
  allowLegacyUnscopedBusiness: boolean;
  excludedProductionIds?: ReadonlySet<string>;
  search?: string;
  now: Date;
}

export interface ProductionCandidateBuckets {
  today: ProductionCandidate[];
  history: ProductionCandidate[];
}

export type ProductionQuantityLimitError =
  | 'invalid_output_qty'
  | 'missing_output_uom'
  | 'missing_food_uom'
  | 'production_food_uom_mismatch'
  | 'missing_balance'
  | 'production_balance_uom_mismatch'
  | 'no_available_stock';

export interface ProductionQuantityLimit {
  declaredQty: number | null;
  availableQty: number | null;
  maxQty: number | null;
  uom: string;
  error: ProductionQuantityLimitError | null;
}

export interface StockLocatorLine {
  production_id?: string | null;
  food_id?: string | null;
  stock_effect_v1?: string | null;
  batch_lot?: string | null;
  qty?: number | string | null;
  uom?: string | null;
}

export interface StockLocatorAvailability {
  food_id: string;
  batch_lot: string;
  available_qty: number;
  uom: string;
}

export type StockAggregateError =
  | 'invalid_line'
  | 'uom_mismatch'
  | 'missing_balance'
  | 'insufficient_available_stock';

export interface StockAggregateValidation {
  valid: boolean;
  error: StockAggregateError | null;
  foodId: string | null;
  batchLot: string | null;
  requestedQty: number | null;
  availableQty: number | null;
  uom: string | null;
}

const ROME_DAY_FORMATTER = new Intl.DateTimeFormat('en', {
  timeZone: 'Europe/Rome',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function cleanText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const clean = value.trim();
  return clean || null;
}

function normalizedUom(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/** The document/stock lot for a production, including new manual productions. */
export function canonicalProductionLot(
  production: Pick<ProductionCandidate, 'id' | 'lp_code'>,
): string {
  return cleanText(production.lp_code) ?? productionBatchLot(production.id);
}

/**
 * Computes the per-production quantity cap. Stock-backed documents require one
 * exact UoM across production, finished food and canonical lot balance.
 */
export function resolveProductionQuantityLimit(input: {
  outputQty: number | null | undefined;
  outputUom: string | null | undefined;
  warehouseEnabled: boolean;
  foodUom?: string | null;
  balanceQty?: number | null;
  balanceUom?: string | null;
}): ProductionQuantityLimit {
  const declaredQty = Number(input.outputQty);
  const outputUom = cleanText(input.outputUom) ?? '';
  if (!Number.isFinite(declaredQty) || declaredQty <= 0) {
    return {
      declaredQty: null,
      availableQty: null,
      maxQty: null,
      uom: outputUom,
      error: 'invalid_output_qty',
    };
  }
  if (!outputUom) {
    return {
      declaredQty,
      availableQty: null,
      maxQty: null,
      uom: '',
      error: 'missing_output_uom',
    };
  }
  if (!input.warehouseEnabled) {
    return {
      declaredQty,
      availableQty: null,
      maxQty: declaredQty,
      uom: outputUom,
      error: null,
    };
  }

  const foodUom = cleanText(input.foodUom) ?? '';
  if (!foodUom) {
    return {
      declaredQty,
      availableQty: null,
      maxQty: null,
      uom: outputUom,
      error: 'missing_food_uom',
    };
  }
  if (normalizedUom(outputUom) !== normalizedUom(foodUom)) {
    return {
      declaredQty,
      availableQty: null,
      maxQty: null,
      uom: outputUom,
      error: 'production_food_uom_mismatch',
    };
  }

  const balanceQty = Number(input.balanceQty);
  const balanceUom = cleanText(input.balanceUom) ?? '';
  if (!Number.isFinite(balanceQty) || !balanceUom) {
    return {
      declaredQty,
      availableQty: null,
      maxQty: null,
      uom: outputUom,
      error: 'missing_balance',
    };
  }
  if (normalizedUom(outputUom) !== normalizedUom(balanceUom)) {
    return {
      declaredQty,
      availableQty: balanceQty,
      maxQty: null,
      uom: outputUom,
      error: 'production_balance_uom_mismatch',
    };
  }

  const availableQty = Math.max(0, balanceQty);
  const maxQty = Math.min(declaredQty, availableQty);
  return {
    declaredQty,
    availableQty,
    maxQty,
    uom: outputUom,
    error: maxQty > 0 ? null : 'no_available_stock',
  };
}

/** Returns only the reservation owned by the draft being edited/issued. */
export function draftReservedQtyForStockLocator(
  lines: readonly StockLocatorLine[],
  locator: { foodId: string; batchLot: string; uom: string },
): number {
  return lines.reduce((total, line) => {
    if (
      line.stock_effect_v1 !== 'dispatch'
      || cleanText(line.food_id) !== locator.foodId
      || cleanText(line.batch_lot) !== locator.batchLot
      || normalizedUom(line.uom) !== normalizedUom(locator.uom)
    ) return total;
    const qty = Number(line.qty);
    return Number.isFinite(qty) && qty > 0 ? total + qty : total;
  }, 0);
}

/** Aggregates every stock-backed line by food+lot and checks the shared cap. */
export function validateStockLocatorTotals(
  lines: readonly StockLocatorLine[],
  availabilities: readonly StockLocatorAvailability[],
): StockAggregateValidation {
  const totals = new Map<string, StockLocatorAvailability>();
  for (const line of lines) {
    if (line.stock_effect_v1 !== 'dispatch') continue;
    const foodId = cleanText(line.food_id);
    const batchLot = cleanText(line.batch_lot);
    const uom = cleanText(line.uom);
    const qty = Number(line.qty);
    if (!foodId || !batchLot || !uom || !Number.isFinite(qty) || qty <= 0) {
      return {
        valid: false,
        error: 'invalid_line',
        foodId,
        batchLot,
        requestedQty: Number.isFinite(qty) ? qty : null,
        availableQty: null,
        uom,
      };
    }
    const key = `${foodId}\u0000${batchLot}`;
    const current = totals.get(key);
    if (current && normalizedUom(current.uom) !== normalizedUom(uom)) {
      return {
        valid: false,
        error: 'uom_mismatch',
        foodId,
        batchLot,
        requestedQty: current.available_qty + qty,
        availableQty: null,
        uom,
      };
    }
    totals.set(key, {
      food_id: foodId,
      batch_lot: batchLot,
      available_qty: (current?.available_qty ?? 0) + qty,
      uom,
    });
  }

  for (const total of totals.values()) {
    const availability = availabilities.find((candidate) => (
      candidate.food_id === total.food_id
      && candidate.batch_lot === total.batch_lot
    ));
    if (!availability) {
      return {
        valid: false,
        error: 'missing_balance',
        foodId: total.food_id,
        batchLot: total.batch_lot,
        requestedQty: total.available_qty,
        availableQty: null,
        uom: total.uom,
      };
    }
    if (normalizedUom(availability.uom) !== normalizedUom(total.uom)) {
      return {
        valid: false,
        error: 'uom_mismatch',
        foodId: total.food_id,
        batchLot: total.batch_lot,
        requestedQty: total.available_qty,
        availableQty: availability.available_qty,
        uom: total.uom,
      };
    }
    if (total.available_qty > availability.available_qty + 1e-9) {
      return {
        valid: false,
        error: 'insufficient_available_stock',
        foodId: total.food_id,
        batchLot: total.batch_lot,
        requestedQty: total.available_qty,
        availableQty: availability.available_qty,
        uom: total.uom,
      };
    }
  }

  return {
    valid: true,
    error: null,
    foodId: null,
    batchLot: null,
    requestedQty: null,
    availableQty: null,
    uom: null,
  };
}

/** Returns the calendar day used by TrackEAT production records in Rome. */
export function productionDayInRome(
  producedAt: string | Date | null | undefined,
): string | null {
  if (producedAt == null) return null;
  const date = producedAt instanceof Date ? producedAt : new Date(producedAt);
  if (!Number.isFinite(date.getTime())) return null;

  const parts = ROME_DAY_FORMATTER.formatToParts(date);
  const byType = new Map(parts.map((part) => [part.type, part.value]));
  const year = byType.get('year');
  const month = byType.get('month');
  const day = byType.get('day');
  return year && month && day ? `${year}-${month}-${day}` : null;
}

function normalizeSearch(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export function matchesProductionCandidateSearch(
  production: ProductionCandidate,
  search: string,
): boolean {
  const query = normalizeSearch(search);
  if (!query) return true;
  return normalizeSearch(production.output_name).includes(query)
    || normalizeSearch(canonicalProductionLot(production)).includes(query);
}

function newestFirst(
  left: ProductionCandidate,
  right: ProductionCandidate,
): number {
  const byTimestamp = new Date(right.produced_at).getTime()
    - new Date(left.produced_at).getTime();
  if (byTimestamp !== 0) return byTimestamp;
  if (left.id < right.id) return -1;
  if (left.id > right.id) return 1;
  return 0;
}

/**
 * Builds the read-only picker sections. Invalid dates and missing active-site
 * context fail closed instead of leaking a production into the wrong bucket.
 */
export function selectProductionCandidates(
  input: SelectProductionCandidatesInput,
): ProductionCandidateBuckets {
  const activeBusinessId = cleanText(input.activeBusinessId);
  const todayKey = productionDayInRome(input.now);
  if (!activeBusinessId || !todayKey) return { today: [], history: [] };

  const today: ProductionCandidate[] = [];
  const history: ProductionCandidate[] = [];
  for (const production of input.productions) {
    if (cleanText(production.archived_at)) continue;
    if (input.excludedProductionIds?.has(production.id)) continue;
    if (!stockEventMatchesBusiness(
      production,
      activeBusinessId,
      input.allowLegacyUnscopedBusiness,
    )) continue;
    if (!matchesProductionCandidateSearch(production, input.search ?? '')) continue;

    const productionDay = productionDayInRome(production.produced_at);
    if (!productionDay) continue;
    // Il bucket storico rappresenta soltanto giornate gia concluse. Una data
    // futura (clock errato/import anomalo) non deve diventare selezionabile come
    // se fosse una lavorazione passata.
    if (productionDay > todayKey) continue;
    (productionDay === todayKey ? today : history).push(production);
  }

  return {
    today: today.sort(newestFirst),
    history: history.sort(newestFirst),
  };
}
